import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Engagement {
  id: string
  name: string
  type: string
  scope: string
  status: string
  start_date: string
  end_date: string
  tester: string
  findings_count: number
  critical_findings: number
}

interface Finding {
  id: string
  engagement_id: string
  title: string
  severity: string
  cvss_score: number
  status: string
  exploitable: boolean
}

interface Statistics {
  total_engagements: number
  active_engagements: number
  total_findings: number
  critical_findings: number
  remediated_findings: number
  avg_remediation_days: number
}

export default function PenetrationTestingPage() {
  const [engagements, setEngagements] = useState<Engagement[]>([])
  const [findings, setFindings] = useState<Finding[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [activeTab, setActiveTab] = useState<'engagements' | 'findings'>('engagements')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [engagementsRes, findingsRes, statsRes] = await Promise.all([
        fetch('/api/pentesting/engagements'),
        fetch('/api/pentesting/findings'),
        fetch('/api/pentesting/statistics')
      ])

      if (engagementsRes.ok) setEngagements(await engagementsRes.json())
      if (findingsRes.ok) setFindings(await findingsRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching penetration testing data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getSeverityBadgeClass = (severity: string) => {
    const classes: Record<string, string> = {
      critical: 'bg-red-100 text-red-800',
      high: 'bg-orange-100 text-orange-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-blue-100 text-blue-800',
      informational: 'bg-gray-100 text-gray-800'
    }
    return classes[severity.toLowerCase()] || 'bg-gray-100 text-gray-800'
  }

  const getStatusBadgeClass = (status: string) => {
    const classes: Record<string, string> = {
      scheduled: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800',
      report_pending: 'bg-purple-100 text-purple-800',
      open: 'bg-red-100 text-red-800',
      in_remediation: 'bg-yellow-100 text-yellow-800',
      verified: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800'
    }
    return classes[status.toLowerCase()] || 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Penetration Testing</h1>
          <p className="mt-2 text-gray-600">Manage security assessments and vulnerability testing</p>
        </div>

        {/* Statistics Cards */}
        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Tests</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.total_engagements}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Active</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.active_engagements}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Findings</p>
              <p className="text-2xl font-bold text-purple-600">{statistics.total_findings}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Critical</p>
              <p className="text-2xl font-bold text-red-600">{statistics.critical_findings}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Remediated</p>
              <p className="text-2xl font-bold text-green-600">{statistics.remediated_findings}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg Fix Days</p>
              <p className="text-2xl font-bold text-orange-600">{statistics.avg_remediation_days}</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('engagements')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'engagements'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Engagements ({engagements.length})
            </button>
            <button
              onClick={() => setActiveTab('findings')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'findings'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Findings ({findings.length})
            </button>
          </nav>
        </div>

        {/* Engagements Table */}
        {activeTab === 'engagements' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tester</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Findings</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dates</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {engagements.map((engagement) => (
                  <tr key={engagement.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <p className="text-sm font-medium text-gray-900">{engagement.name}</p>
                      <p className="text-sm text-gray-500">{engagement.scope}</p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {engagement.type}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeClass(engagement.status)}`}>
                        {engagement.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {engagement.tester}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{engagement.findings_count}</span>
                      {engagement.critical_findings > 0 && (
                        <span className="ml-2 px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full">
                          {engagement.critical_findings} critical
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {engagement.start_date} - {engagement.end_date}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Findings Table */}
        {activeTab === 'findings' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Severity</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">CVSS</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Exploitable</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {findings.slice(0, 20).map((finding) => (
                  <tr key={finding.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <p className="text-sm font-medium text-gray-900">{finding.title}</p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getSeverityBadgeClass(finding.severity)}`}>
                        {finding.severity}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`text-sm font-bold ${finding.cvss_score >= 9 ? 'text-red-600' : finding.cvss_score >= 7 ? 'text-orange-600' : 'text-yellow-600'}`}>
                        {finding.cvss_score.toFixed(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeClass(finding.status)}`}>
                        {finding.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {finding.exploitable ? (
                        <span className="text-red-600 font-medium">Yes</span>
                      ) : (
                        <span className="text-gray-400">No</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  )
}
